// Section R (176): every word on "Found in the documents" and the toggle, ONCE (the words list 6a7d9399, cleared by
// the advisor A-#596 4, with F4's change; Q1 keep the page name; Q2 "Can't read it"). Each is a constant, so a later
// ruling changes one line; a test reads them from here.
import type { ReviewKind, ReviewRow } from './api/readingReview.ts';
import { agencyDay, formatDate } from './dates.ts';
import { STRUCK_WORDS } from './pages/strikeOrder.ts';
import { strikeReasonWords } from './strikeReasonWords.ts';

export const PAGE_TITLE = 'Found in the documents';
export const PAGE_LINE = 'What the court papers already show, and what Procura\'s own records already prove. Nothing here '
  + 'changes a record until someone confirms it, or a rule turned on in Company policies does.';

export type KindCode = 'R_OATH' | 'R_COMMISSION' | 'R_MARSHAL' | 'P_OATH' | 'P_COMMISSION' | 'DISCHARGE' | 'ORDER' | 'OSC_DATE';
export const RECORD_KINDS: KindCode[] = ['R_OATH', 'R_COMMISSION', 'R_MARSHAL'];
export const CONFIRMABLE: KindCode[] = ['R_OATH', 'R_COMMISSION', 'R_MARSHAL', 'P_OATH', 'P_COMMISSION'];

// P_COMMISSION (180; A-#787 1, A-#791 2): what the strike pass found on the date's page, in WORDS (never colour alone;
// Arthur, 21 October). A row with no state (every other kind) is unaffected by any of this.
export type StrikeState = 'date_struck' | 'page_unchecked' | 'not_checked' | 'other_struck' | 'clear';
export const STRIKE_STATES: StrikeState[] = ['date_struck', 'page_unchecked', 'not_checked', 'other_struck', 'clear'];
const NOT_CHECKED_WHY: Record<string, string> = {
  no_marker: 'this reading of the paper has not been checked',
  no_date_page: 'the date\'s page is not known',
};
export const strikeLine = (state: StrikeState, why: string | null | undefined): string =>
  state === 'date_struck' ? `${STRUCK_WORDS.all}: a date on this page. Read the page and record the commission on the order.`
  : state === 'page_unchecked' ? `Not checked for a line through it: ${strikeReasonWords(why ?? 'check_failed')}`
  : state === 'not_checked' ? `Not checked for a line through it: ${NOT_CHECKED_WHY[why ?? ''] ?? NOT_CHECKED_WHY.no_marker}`
  : state === 'other_struck' ? 'Other words on this page are struck through; the date is not.'
  : 'Checked: nothing on this page is struck through.';
/** "Select all" takes these only; a row in any other state is never sent by it. */
export const inSelectAll = (r: ReviewRow) => r.strike == null || r.strike === 'clear';
/** A row with its own box (a struck date has none; a page not checked is confirmed on its own, beside the page). */
export const hasBox = (r: ReviewRow) => r.strike == null || r.strike === 'clear' || r.strike === 'other_struck';
/** A-#791 2 (2): confirmed one at a time, only once its paper has been opened here. */
export const confirmedSingly = (r: ReviewRow) => r.strike === 'page_unchecked' || r.strike === 'not_checked';
export const CONFIRM_ONE = 'Confirm this one';
export const OPEN_FIRST = 'Open the paper first: its page was not checked for a line through it.';
/** The tab's own count by state, of the rows the reader can see (A-#791 2 (3)). */
export const stateCount = (rows: readonly ReviewRow[]) => {
  const n = (s: StrikeState) => rows.filter((r) => r.strike === s).length;
  return `${n('date_struck')} struck through, ${n('page_unchecked') + n('not_checked')} not checked for a line, `
    + `${n('other_struck')} with other words struck, ${n('clear')} clear`;
};

export const TAB_COUNT = (n: number) => (n === 0 ? 'Nothing to look at' : `${n} to look at`);

// W2: a kind not open yet is a state, not a failure. The server says so with a 409 in its own words (no kind does
// since 180 opened commissions from the papers; kept for the next kind that waits); the tab shows those words and
// nothing that says something broke. Read by shape, as api/client.ts's ApiError is made (name 'ApiError', a status), so this stays
// free of the sign-in module; any other error is not this, and stays a load failure.
export const notYetOpen = (error: unknown): string | null =>
  error instanceof Error && error.name === 'ApiError' && (error as Error & { status?: unknown }).status === 409
    ? error.message : null;
export const CLOSED_BY_RULE = 'Closed by rule';

export const proofRecord = (kind: KindCode, date: string) =>
  kind === 'R_MARSHAL' ? `Recorded in marshalling: ${date}` : `Already on the order: ${date}`;
export const proofPaper = (page: number | null, type: string) => (page ? `Page ${page} of the ${type}` : `The ${type}`);
export const NEEDS_A_PERSON = (why: string) => `Needs a person: ${why}`;

export const SELECT_ROW = 'Select this row';
export const SELECT_ALL = 'Select all on this page';
export const CONFIRM_SELECTED = 'Confirm selected';
export const confirmSummary = (n: number, kindPlural: string) =>
  `Record ${n} ${kindPlural}, each from its own record or document, with the document attached where there is one?`;
export const CONFIRM = 'Confirm';
export const GO_BACK = 'Go back';
export const RECORDED = 'Recorded';
export const notRecorded = (why: string) => `Not recorded: ${why}`;
export const resultTotal = (ok: number, not: number) => `${ok} recorded, ${not} not recorded`;

export const NOT_RIGHT = 'Not right';
export const NOT_RIGHT_QUESTION = 'What is wrong with it?';
export const NOT_RIGHT_REASONS: { code: 'wrong_client' | 'wrong_order' | 'not_the_date' | 'unreadable'; words: string }[] = [
  { code: 'wrong_client', words: 'Wrong client' },
  { code: 'wrong_order', words: 'Wrong order' },
  { code: 'not_the_date', words: 'Not the date' },
  { code: 'unreadable', words: 'Can\'t read it' },
];
export const NOT_RIGHT_DONE = 'Recorded as not right. It will not be offered again unless the paper is read again.';
export const NOT_RIGHT_DONE_CLOSED = 'Recorded as not right. To undo the close, correct the record itself on the client\'s Legal page.';

// the rows a confirm sends: those picked on this page that have a deadline; the question names this number, never
// how many boxes are ticked (L2)
export const rowKey = (r: ReviewRow) => r.deadline_id ?? `${r.client?.id}:${r.clio_copy_id}`;
export const rowsToSend = (page: ReviewRow[], picked: ReadonlySet<string>) =>
  page.filter((r) => r.deadline_id && hasBox(r) && picked.has(rowKey(r)))
    .map((r) => ({ deadline_id: r.deadline_id!, field_id: r.field_id ?? null }));

// the day a kind was turned on, in the agency's clock (L1): the setting's time is an instant
export const onSince = (setting: { at: string }) => formatDate(agencyDay(setting.at));

// the page's read-only status per kind (A-#608 5), linking to Company policies, where the toggle is
export const statusOn = (date: string) => `Closing by rule: on since ${date}`;
export const STATUS_OFF = 'Closing by rule: off';
export const kindStatus = (k: ReviewKind) => (k.on && k.setting ? statusOn(onSince(k.setting)) : STATUS_OFF);
export const STATUS_LINK = 'Company policies';
export const STATUS_HREF = '/policies?tab=tasks';

// the toggle (Company policies; SYSADMIN only)
export const TOGGLE_HEADING = 'Closing by rule';
export const TOGGLE_LINE = 'Closes, every day, the rows of a kind that is turned on, exactly as a person\'s confirm would, '
  + 'recorded as the system. Turning a kind off stops it closing more; nothing it closed is reopened.';
export const toggleOn = (label: string, date: string, name: string) => `${label}: on since ${date}, by ${name}`;
export const toggleOff = (label: string) => `${label}: off`;
export const toggleNever = (label: string) => `${label}: a person always records this`;
export const toggleLine = (k: ReviewKind) => (!k.may_auto ? toggleNever(k.label)
  : k.on && k.setting ? toggleOn(k.label, onSince(k.setting), k.setting.by ?? '')
  : toggleOff(k.label));
export const COMMISSION_EVIDENCE = 'Read dates differed from the record on 14 percent of commissions (39 of 274 where '
  + 'both exist, inventory v3, 29 October).';
export const yardstick = (n: number, m: number) => `The reading agrees with the record on ${n} of ${m}`;
export const TURN_ON = 'Turn on';
export const TURN_OFF = 'Turn off';
export const WHY_NOW = 'Why now?';
